import { useState } from 'react'
import type { CSSProperties } from 'react'
import { useLocale } from '../i18n/useLocale'
import {
  eligibilityReport, markEligibilityReported, openSupportMailto, type MismatchReason,
} from '../data/support'
import { loadSession } from '../auth/session'
import { showNotWiredToast } from '../lib/notWiredToast'

/*
 * Eligibility-mismatch banner + the "always an out" (Duncan rulings
 * BOARDROOM-2026-08-29-120259 #2/#3 + ADDENDUM 122136 + ADDENDUM 2 123126).
 * ONE component serves BOTH surfaces — the get-ready event card and the
 * profile wizard's inline fail-fast (gender AND age axes) — so the two
 * sub-states behave identically everywhere:
 *
 *  1. BEFORE reporting: amber banner carrying the mismatch copy + the button
 *     "Submit a support ticket so we can help you resolve this" (verbatim per
 *     ruling 2), wired to the support-intake seam (src/data/support.ts) with
 *     the pre-fill — ticket id, event, participant, mismatch reason — so
 *     support needs no interrogation.
 *  1b. COMPOSE/CONFIRM INTERLOCK (CMT-2026-09-03-021, Duncan's live-walk
 *     defect: one click instantly asserted "support request submitted" — an
 *     empty/phantom ticket with no compose/confirm; on a handlerless browser
 *     the mailto no-ops silently, so nothing was sent at all). The click now
 *     ONLY opens the pre-filled compose and moves to an intermediate
 *     "email opened — send it, then confirm" state; the submitted record is
 *     written EXCLUSIVELY on the participant's explicit "I've sent the
 *     support email" confirm. NEVER on the bare click.
 *  2. AFTER confirming: "You currently don't have a valid ticket … — support
 *     request submitted" + the honest reference (ticket id + report date),
 *     NO button — no re-nag — plus a LOW-KEY "Open the support email again"
 *     link (P1-1): compose-without-send stays undetectable from JS, so the
 *     out must never disappear with the button. Re-opening never touches the
 *     record. Persistence is DEVICE-LOCAL (see support.ts: my-api has no
 *     support-intake seam; nothing is faked as a server write).
 *
 * When no support address is configured (VITE_SUPPORT_EMAIL unset) the click
 * shows the honest not-wired toast and nothing advances — the reported state
 * only ever asserts a compose the participant confirmed sending.
 */

export function EligibilityMismatchNotice({ ticketId, eventName, copy, reason, participantName }: {
  ticketId: string
  eventName: string
  /** Locale-resolved mismatch copy (participant voice, reason-aware). */
  copy: string
  reason: MismatchReason
  participantName?: string
}) {
  const { t, lang } = useLocale()
  // Seeded from the device-local store; the CONFIRM below is the only writer.
  const [report, setReport] = useState(() => eligibilityReport(ticketId))
  // COMPOSE/CONFIRM interlock (CMT-2026-09-03-021): true once the pre-filled
  // compose was opened this render-session — the confirm affordance then
  // gates the reported record. Deliberately TRANSIENT (never persisted): an
  // un-confirmed compose leaves the banner + out intact on the next visit,
  // because nothing was verifiably sent.
  const [composed, setComposed] = useState(false)

  const barStyle: CSSProperties = {
    display: 'flex', flexDirection: 'column', gap: 9, padding: '11px 16px',
    background: 'rgba(230,193,90,.1)', borderBlockStart: '1px solid rgba(230,193,90,.28)',
  }
  const copyStyle: CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 8,
    fontSize: 11.5, lineHeight: 1.45, color: 'var(--tru-status-amber, var(--warn))', fontWeight: 700,
  }

  // ONE compose path for the first attempt AND the re-open link, so the
  // pre-fill can never drift between the two.
  const openCompose = () =>
    openSupportMailto({
      ticketId,
      eventName,
      participantName,
      participantEmail: loadSession()?.claims?.email,
      reason: copy,
      reasonCode: reason,
    })

  // FIX CMT-2026-09-03-021 (Duncan's live walk): the click must open the
  // COMPOSE seam and stop there — it must NEVER submit/record a ticket by
  // itself (the old path instantly wrote the "support request submitted"
  // record: an empty phantom ticket when the mailto silently no-oped). The
  // record is written only by the explicit confirm below.
  const onSupport = () => {
    if (!openCompose()) {
      // Honest not-wired signal (the repo's inert-control rule) — and nothing
      // advances: no compose exists to confirm.
      showNotWiredToast()
      return
    }
    setComposed(true)
  }

  // The ONLY writer of the reported record (CMT-2026-09-03-021): the
  // participant's own assertion that the pre-filled email was sent. Still an
  // honest device-local record — "confirmed sent by the participant", never a
  // server intake claim (see support.ts).
  const onConfirmSent = () => {
    setReport(markEligibilityReported(ticketId, reason))
  }

  // Sub-state 2's re-open (P1-1): re-invokes the same compose; NEVER writes
  // the record — the reported state (and its date) describe the first attempt.
  const onReopen = () => {
    if (!openCompose()) showNotWiredToast()
  }

  if (report) {
    // Sub-state 2 — reported. Reference = real facts (ticket + date), never a
    // fabricated case number. Date renders in the active locale.
    const date = new Date(report.at)
    const dateText = Number.isNaN(date.getTime())
      ? report.at
      : date.toLocaleDateString(lang === 'ar' ? 'ar' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    return (
      <div data-testid="elig-mismatch-reported" role="status" style={barStyle}>
        <span style={copyStyle}>
          <span aria-hidden style={{ flex: 'none', fontSize: 13 }}>⚠</span>
          {t('eligibility.support.reported')}
        </span>
        <span data-testid="elig-mismatch-ref" style={{ fontSize: 11, color: 'var(--tru-text-tertiary, var(--text-secondary))' }}>
          {t('eligibility.support.reportedRef', { ticket: ticketId, date: dateText })}
        </span>
        {/* LOW-KEY re-open link (P1-1): the "always an out" must survive a
            device whose mailto attempt silently no-oped. Deliberately quiet —
            a text link, not the CTA button (Duncan's ruled sentence + no-re-nag
            stand); clicking it never changes the recorded report. */}
        <button
          data-testid="elig-mismatch-reopen"
          onClick={onReopen}
          style={{
            alignSelf: 'start', border: 0, background: 'transparent', padding: 0,
            minHeight: 24, fontFamily: 'inherit', fontSize: 11, fontWeight: 600,
            color: 'var(--tru-text-tertiary, var(--text-secondary))',
            textDecoration: 'underline', cursor: 'pointer', textAlign: 'start',
          }}
        >
          {t('eligibility.support.reopen')}
        </button>
      </div>
    )
  }

  // Sub-state 1b — compose opened, awaiting the participant's send-confirm
  // (CMT-2026-09-03-021). The mismatch copy stays visible; the record is NOT
  // yet written — abandoning here leaves the out intact next visit.
  if (composed) {
    return (
      <div data-testid="elig-mismatch-composed" role="status" style={barStyle}>
        <span style={copyStyle}>
          <span aria-hidden style={{ flex: 'none', fontSize: 13 }}>⚠</span>
          {copy}
        </span>
        <span style={{ fontSize: 11, color: 'var(--tru-text-tertiary, var(--text-secondary))' }}>
          {t('eligibility.support.composed')}
        </span>
        <button
          data-testid="elig-mismatch-confirm"
          onClick={onConfirmSent}
          style={{
            alignSelf: 'start', border: '1px solid rgba(230,193,90,.45)', borderRadius: 9,
            minHeight: 40, padding: '0 14px', background: 'transparent',
            color: 'var(--tru-status-amber, var(--warn))', fontFamily: 'inherit',
            fontSize: 12, fontWeight: 800, cursor: 'pointer', textAlign: 'start',
          }}
        >
          {t('eligibility.support.confirm')}
        </button>
        {/* The compose can be re-opened without touching any record. */}
        <button
          data-testid="elig-mismatch-reopen"
          onClick={onReopen}
          style={{
            alignSelf: 'start', border: 0, background: 'transparent', padding: 0,
            minHeight: 24, fontFamily: 'inherit', fontSize: 11, fontWeight: 600,
            color: 'var(--tru-text-tertiary, var(--text-secondary))',
            textDecoration: 'underline', cursor: 'pointer', textAlign: 'start',
          }}
        >
          {t('eligibility.support.reopen')}
        </button>
      </div>
    )
  }

  // Sub-state 1 — unreported: the mismatch copy + the out.
  return (
    <div data-testid="elig-mismatch-banner" role="alert" style={barStyle}>
      <span style={copyStyle}>
        <span aria-hidden style={{ flex: 'none', fontSize: 13 }}>⚠</span>
        {copy}
      </span>
      <button
        data-testid="elig-mismatch-support-cta"
        onClick={onSupport}
        style={{
          alignSelf: 'start', border: '1px solid rgba(230,193,90,.45)', borderRadius: 9,
          minHeight: 40, padding: '0 14px', background: 'transparent',
          color: 'var(--tru-status-amber, var(--warn))', fontFamily: 'inherit',
          fontSize: 12, fontWeight: 800, cursor: 'pointer', textAlign: 'start',
        }}
      >
        {t('eligibility.support.cta')}
      </button>
    </div>
  )
}
